import "proxy-polyfill";
import "../node_modules/bootstrap/dist/css/bootstrap.min.css";
import "../src/components/GlobalCss/globalStyle.css";
import "react-date-range/dist/styles.css";
import "react-date-range/dist/theme/default.css";
import "react-phone-input-2/lib/style.css";
import ReactDOM from "react-dom/client";
import { Provider } from "react-redux";
import { OpenAPI } from "@propertelligent/client-api";
import App from "./App";
import { BrowserRouter, HashRouter } from "react-router-dom";
import { MyProvider } from "./utils/context/LoginContext";
import { PermissionProvider } from "./utils/context/PermissionContext";
import { PropertyProvider } from "./utils/context/PropertyProvider";
import { MenuProvider } from "./utils/context/MenuContext";
import { ThemeProvider } from "./utils/context/ThemeContext";
import store from "./store";
import { QuickstartProvider } from "./components/firstTimeUserExperience/Context";
import { ErrorProvider } from "./utils/context/ErrorContext";
import { OverviewProvider } from "./utils/context/OverviewContext";
import { GeneratePdfProvider } from "./utils/context/GeneratePdfContext";
import { PaymentProvider } from "./utils/context/PaymentContext";
import { OnboardingProvider } from "./utils/context/OnboardingContext";
import {
  NotificationPayload,
  NotificationType,
  PubSub,
  TopicsEnum,
} from "./utils/pubsub";
import { isElectron, hydrateDesktopAuth } from "./utils/electron/desktop";
import { resolveLocalViteOpenApiBase } from "./utils/resolveLocalViteOpenApiBase";

const root = ReactDOM.createRoot(document.getElementById("root")!);

// Electron desktop build: the renderer loads over app:// (packaged) or the
// Vite dev server, so "same origin" no longer points at the API and the HTML5
// history API can't back path-based routes off disk. Use HashRouter; the
// absolute dev API base + auth hydration happen in bootstrap() below. In a
// normal browser isElectron is false, so the web/wwwroot build is unaffected.
const Router = isElectron ? HashRouter : BrowserRouter;

function handleClickOutside(event: MouseEvent) {
  PubSub.publish(TopicsEnum.BodyMouseDown.toString(), {
    type: NotificationType.BodyMouseDown,
    message: "",
    event, // <- Pass the actual event
  } as any); // Use `as any` if TypeScript complains
}

document.addEventListener("mousedown", handleClickOutside);

const renderApp = () =>
  root.render(
  <ErrorProvider>
    <ThemeProvider>
      <MyProvider>
        <PermissionProvider>
          <PaymentProvider>
            <GeneratePdfProvider>
              <OverviewProvider>
                <OnboardingProvider>
                  <Provider store={store}>
                    <PropertyProvider>
                      <QuickstartProvider>
                        <MenuProvider>
                          <Router>
                            <App />
                          </Router>
                        </MenuProvider>
                      </QuickstartProvider>
                    </PropertyProvider>
                  </Provider>
                </OnboardingProvider>
              </OverviewProvider>
            </GeneratePdfProvider>
          </PaymentProvider>
        </PermissionProvider>
      </MyProvider>
    </ThemeProvider>
  </ErrorProvider>,
);

async function bootstrap() {
  if (isElectron) {
    // Desktop always talks to the dev backend; hydrate the persisted session
    // before first render so App.tsx starts from a warm (possibly logged-in)
    // sessionStorage.
    OpenAPI.BASE = "https://dev.propertelligent.com";
    await hydrateDesktopAuth();
  } else {
    // Opt-in only on localhost/127.0.0.1 via VITE_API_BASE_URL; otherwise leave
    // the generated client default (or "" for non-local deploys). See helper.
    const resolved = resolveLocalViteOpenApiBase(
      window.location.href,
      import.meta.env.VITE_API_BASE_URL
    );
    if (resolved !== undefined) {
      OpenAPI.BASE = resolved;
    }
  }
  renderApp();
}

bootstrap().catch((e) => console.error("[bootstrap] failed", e));
